/**
 * Progress Calculation Utilities
 * 
 * Centralized utilities for calculating progress percentages, determining status levels,
 * and formatting progress displays across the application. This module eliminates
 * duplication of progress calculation logic found in multiple components.
 */

// ============================================================================
// TYPES AND INTERFACES
// ============================================================================

/**
 * Progress status levels based on completion percentage
 */
export type ProgressStatus = 'excellent' | 'good' | 'warning' | 'critical';

/**
 * Workload level categories for team member assignments
 */
export type WorkloadLevel = 'High Load' | 'Medium Load' | 'Low Load' | 'No Work';

/**
 * Color mappings for different progress/workload states
 */
export type ProgressColor = 'success' | 'primary' | 'warning' | 'error' | 'default';

/**
 * Comprehensive progress data structure
 */
export interface ProgressData {
  /** Number of completed items/points */
  completed: number;
  /** Total number of items/points */
  total: number;
  /** Calculated percentage (0-100) */
  percentage: number;
  /** Status level based on percentage */
  status: ProgressStatus;
  /** Color indicator for UI components */
  color: ProgressColor;
}

/**
 * Workload analysis data structure
 */
export interface WorkloadData {
  /** Individual workload amount */
  amount: number;
  /** Total pool for percentage calculation */
  totalPool: number;
  /** Calculated percentage of total workload */
  percentage: number;
  /** Workload level category */
  level: WorkloadLevel;
  /** Color indicator for UI components */
  color: ProgressColor;
}

// ============================================================================
// CORE CALCULATION FUNCTIONS
// ============================================================================

/**
 * Calculate progress percentage with optional precision control
 * @param completed - Number of completed items/points
 * @param total - Total number of items/points
 * @param precision - Number of decimal places (default: 1)
 * @returns Progress percentage (0-100), clamped to valid range
 */
export const calculateProgressPercentage = (
  completed: number, 
  total: number,
  precision: number = 1
): number => {
  if (total === 0) return 0;
  const percentage = (completed / total) * 100;
  const clampedPercentage = Math.min(100, Math.max(0, percentage));
  return Number(clampedPercentage.toFixed(precision));
};

/**
 * Calculate comprehensive progress data with status and color
 * @param completed - Number of completed items/points
 * @param total - Total number of items/points
 * @param precision - Number of decimal places (default: 1)
 * @returns Complete progress data structure
 */
export const calculateProgress = (
  completed: number, 
  total: number,
  precision: number = 1
): ProgressData => {
  const percentage = calculateProgressPercentage(completed, total, precision);
  const status = getProgressStatus(percentage);
  const color = getProgressColor(percentage);
  
  return {
    completed,
    total,
    percentage,
    status,
    color
  };
};

// ============================================================================
// STATUS AND COLOR DETERMINATION
// ============================================================================

/**
 * Determine progress status based on completion percentage
 * @param percentage - Progress percentage (0-100)
 * @returns Status level
 */
export const getProgressStatus = (percentage: number): ProgressStatus => {
  if (percentage >= 90) return 'excellent';
  if (percentage >= 70) return 'good';
  if (percentage >= 40) return 'warning';
  return 'critical';
};

/**
 * Get color indicator for progress percentage
 * @param percentage - Progress percentage (0-100)
 * @returns Color indicator for UI components
 */
export const getProgressColor = (percentage: number): ProgressColor => {
  if (percentage >= 80) return 'success';
  if (percentage >= 60) return 'primary';
  if (percentage >= 40) return 'warning';
  return 'error';
};

// ============================================================================
// WORKLOAD ANALYSIS FUNCTIONS
// ============================================================================

/**
 * Calculate workload level and percentage for team member assignments
 * Uses dynamic thresholds based on team size to determine if workload is fairly distributed.
 *
 * The calculation compares each member's workload to their "fair share" (100% / teamSize):
 * - High Load: >1.5x fair share (50% above expected)
 * - Medium Load: >1.2x fair share (20% above expected)
 * - Low Load: ≤1.2x fair share (at or below expected)
 *
 * Example: In a 5-person team, fair share = 20%
 * - High Load: >30% (1.5 × 20%)
 * - Medium Load: >24% (1.2 × 20%)
 * - Low Load: ≤24%
 *
 * @param memberAmount - Individual member's workload amount
 * @param totalPool - Total workload pool for percentage calculation
 * @param teamSize - Number of team members (default: 1 for backward compatibility)
 * @returns Workload analysis data
 */
export const calculateWorkloadLevel = (
  memberAmount: number,
  totalPool: number,
  teamSize: number = 1
): WorkloadData => {
  if (totalPool === 0) {
    return {
      amount: memberAmount,
      totalPool,
      percentage: 0,
      level: 'No Work',
      color: 'default'
    };
  }

  const percentage = (memberAmount / totalPool) * 100;

  // Calculate fair share based on team size
  // Handle edge cases: minimum team size of 1
  const effectiveTeamSize = Math.max(1, teamSize);
  const fairShare = 100 / effectiveTeamSize;

  // Dynamic thresholds based on fair share
  const highLoadThreshold = fairShare * 1.5;  // 50% above fair share
  const mediumLoadThreshold = fairShare * 1.2; // 20% above fair share

  let level: WorkloadLevel;
  let color: ProgressColor;

  if (percentage > highLoadThreshold) {
    level = 'High Load';
    color = 'error';
  } else if (percentage > mediumLoadThreshold) {
    level = 'Medium Load';
    color = 'warning';
  } else if (percentage > 0) {
    level = 'Low Load';
    color = 'success';
  } else {
    level = 'No Work';
    color = 'default';
  }

  return {
    amount: memberAmount,
    totalPool,
    percentage: Number(percentage.toFixed(1)),
    level,
    color
  };
};

// ============================================================================
// FORMATTING AND DISPLAY UTILITIES
// ============================================================================

/**
 * Format progress data for display
 * @param progress - Progress data structure
 * @returns Formatted string representation
 */
export const formatProgressDisplay = (progress: ProgressData): string => {
  return `${progress.completed}/${progress.total} (${progress.percentage}%)`;
};

/**
 * Format workload data for display
 * @param workload - Workload data structure
 * @returns Formatted string representation
 */
export const formatWorkloadDisplay = (workload: WorkloadData): string => {
  return `${workload.level} (${workload.percentage}%)`;
};

/**
 * Format simple percentage for display
 * @param completed - Number of completed items
 * @param total - Total number of items
 * @param precision - Number of decimal places (default: 1)
 * @returns Formatted percentage string
 */
export const formatPercentage = (
  completed: number, 
  total: number, 
  precision: number = 1
): string => {
  const percentage = calculateProgressPercentage(completed, total, precision);
  return `${percentage}%`;
};

// ============================================================================
// LEGACY COMPATIBILITY FUNCTIONS
// ============================================================================

/**
 * Legacy compatibility function for existing components
 * @deprecated Use calculateProgressPercentage instead
 */
export const calculateProgress_Legacy = calculateProgressPercentage;

/**
 * Legacy completion rate calculation (used in TeamWorkloadChart)
 * @param completed - Completed story points
 * @param total - Total story points
 * @returns Completion rate percentage
 */
export const calculateCompletionRate = (completed: number, total: number): number => {
  return calculateProgressPercentage(completed, total, 1);
};
